const RELOADED_KEY = 'tapybl-chunk-reload';

/**
 * Runs a lazy import; when its chunk is gone because a new build was deployed while the tab was
 * open, reloads the page once so it loads the new build. A second failure in the same tab is thrown.
 */
export async function reloadOnChunkError<T>(
  load: () => Promise<T>,
): Promise<T> {
  try {
    const module = await load();
    try {
      sessionStorage.removeItem(RELOADED_KEY);
    } catch {
      // Storage can be blocked; the import still worked.
    }
    return module;
  } catch (error) {
    if (!isChunkError(error)) throw error;
    let reloaded = true;
    try {
      reloaded = sessionStorage.getItem(RELOADED_KEY) !== null;
      if (!reloaded) sessionStorage.setItem(RELOADED_KEY, '1');
    } catch {
      // Without storage a reload could repeat forever, so the error is shown instead.
    }
    if (reloaded) throw error;
    window.location.reload();
    // The page is going away; never resolve so nothing renders the error first.
    return new Promise<T>(() => undefined);
  }
}

export function isChunkError(error: unknown) {
  // Webpack's ChunkLoadError is not always an Error instance, so name and message are read from any object.
  const {name = '', message = String(error)} =
    typeof error === 'object' && error !== null
      ? (error as {name?: string; message?: string})
      : {};
  return /ChunkLoadError|Loading (CSS )?chunk \S+ failed/i.test(
    `${name} ${message}`,
  );
}
